// File: components/search/SearchBar.js

import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Clock } from 'lucide-react';
import { debounce } from '../../utils/helpers';
import { getSearchHistory, addSearchHistory } from '../../utils/storage';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * SearchBar Component
 * 
 * Mobile-first search input with autocomplete, history, and clear functionality.
 * Features debounced search, keyboard navigation, and touch-optimized interactions.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current search value
 * @param {function} props.onChange - Handler for search value changes
 * @param {function} props.onSearch - Handler for search submission
 * @param {function} props.onClear - Handler for clear action
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.autoFocus - Auto focus on mount
 * @param {boolean} props.showHistory - Show search history
 * @param {number} props.debounceMs - Debounce delay in milliseconds
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.className - Additional CSS classes
 */
const SearchBar = ({
    value = '',
    onChange,
    onSearch,
    onClear,
    placeholder = null,
    autoFocus = false,
    showHistory = true,
    debounceMs = 300,
    disabled = false,
    className = ''
}) => {
    const { t } = useLanguage();
    const effectivePlaceholder = placeholder || t('search.placeholder');
    const [inputValue, setInputValue] = useState(value);
    const [isFocused, setIsFocused] = useState(false);
    const [searchHistory, setSearchHistory] = useState([]);
    const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    // Load search history on mount
    useEffect(() => {
        if (showHistory) {
            const history = getSearchHistory();
            setSearchHistory(history);
        }
    }, [showHistory]);

    // Sync with external value changes
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Auto focus if requested
    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    // Handle click outside to close history dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowHistoryDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced change handler
    const debouncedOnChange = useRef(
        debounce((newValue) => {
            if (onChange) {
                onChange(newValue);
            }
        }, debounceMs)
    ).current;

    // Handle input change
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        debouncedOnChange(newValue);
    };

    // Handle search submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!inputValue.trim()) return;

        // Add to search history
        if (showHistory) {
            addSearchHistory(inputValue.trim());
            setSearchHistory(getSearchHistory());
        }

        // Close history dropdown
        setShowHistoryDropdown(false);

        // Blur input on mobile
        if (inputRef.current) {
            inputRef.current.blur();
        }

        // Call search handler
        if (onSearch) {
            onSearch(inputValue.trim());
        }
    };

    // Handle clear
    const handleClear = () => {
        setInputValue('');
        setShowHistoryDropdown(false);
        
        if (onChange) {
            onChange('');
        }
        
        if (onClear) {
            onClear();
        }

        // Focus input after clear
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Handle focus
    const handleFocus = () => {
        setIsFocused(true);
        if (showHistory && searchHistory.length > 0 && !inputValue) {
            setShowHistoryDropdown(true);
        }
    };

    // Handle blur
    const handleBlur = () => {
        setIsFocused(false);
        // Delay to allow click on history items
        setTimeout(() => {
            setShowHistoryDropdown(false);
        }, 200);
    };

    // Handle history item click
    const handleHistoryClick = (historyItem) => {
        setInputValue(historyItem);
        setShowHistoryDropdown(false);
        
        if (onChange) {
            onChange(historyItem);
        }
        
        if (onSearch) {
            onSearch(historyItem);
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            handleClear();
        }
    };

    return (
        <div 
            className={`search-bar-container ${className}`}
            ref={containerRef}
        >
            <form 
                className={`search-bar ${isFocused ? 'focused' : ''} ${disabled ? 'disabled' : ''}`}
                onSubmit={handleSubmit}
            >
                <button
                    type="submit"
                    className="search-icon-button"
                    aria-label="Search"
                    disabled={disabled || !inputValue.trim()}
                >
                    <Search size={20} />
                </button>

                <input
                    ref={inputRef}
                    type="text"
                    className="search-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={effectivePlaceholder}
                    disabled={disabled}
                    aria-label={t('title.search')}
                />

                {inputValue && (
                    <button
                        type="button"
                        className="clear-button"
                        onClick={handleClear}
                        aria-label={t('search.clear')}
                    >
                        <X size={18} />
                    </button>
                )}
            </form>

            {/* Search History Dropdown */}
            {showHistory && showHistoryDropdown && searchHistory.length > 0 && (
                <div className="search-history-dropdown">
                    <div className="search-history-header">
                        <Clock size={14} />
                        <span>{t('search.recent')}</span>
                    </div>
                    <div className="search-history-list">
                        {searchHistory.map((item, index) => (
                            <button
                                key={index}
                                type="button"
                                className="search-history-item"
                                onClick={() => handleHistoryClick(item)}
                            >
                                <Search size={14} />
                                <span className="text">{item}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchBar;
